/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import React, { useState, useRef, useEffect } from 'react';
import { IMoeAudioPlayerViewProps } from './moe-audio-player';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { FiVolume2 } from 'react-icons/fi';
import { FaPlay, FaPause, FaVolumeMute, FaDownload } from 'react-icons/fa';
import { AiFillBackward, AiFillForward, AiOutlinePlus } from 'react-icons/ai';
import { IoMdVolumeHigh } from 'react-icons/io';
import { MdOutlineSubject } from 'react-icons/md';
import { saveAs } from 'file-saver';
import * as MsDyn365 from '@msdyn365-commerce/core';
import { getToggleLang } from '../../utilities/language-change';
import { MAORI_LANG } from '../../utilities/constants';

export default (props: IMoeAudioPlayerViewProps) => {
    const { AudioPlayerContainer, AudioPlayerTag, config, context, resources } = props;

    // State
    const [isPlaying, setIsPlaying] = useState<any>(false);
    const [duration, setDuration] = useState<any>('00:00');
    const [showTranscript, setShowTranscript] = useState<any>(false);
    const [currentTime, setCurrentTime] = useState<any>('00:00');
    const [isMuted, setIsMuted] = useState(false);

    // Language translation
    const toggleLang = getToggleLang(context);

    // references
    const audioPlayer: any = useRef();
    const progressBar: any = useRef();
    const animationRef: any = useRef();

    useEffect(() => {
        getTotalDuration();
    }, [audioPlayer?.current?.readyState]);

    // Variables
    const transcript = config.audioTranscript;
    const audioTitle = config.audioTitle;
    const audioDesc = config.audioDesc;
    const audioUrl = config.audioUrl;
    const audioFileName = config.audioFileName;

    const getTotalDuration = () => {
        const secs = Math.floor(audioPlayer.current.duration);
        progressBar.current.max = secs;
        const totalDuration = calculateTime(secs);
        if (totalDuration === 'NaN:NaN') {
            setDuration('00:00');
            return;
        }
        setDuration(totalDuration);
    };

    const getCurrentTime = () => {
        const secs = progressBar.current.value;
        const max = progressBar.current.max;
        audioPlayer.current.currentTime = secs;
        const currentDuration = calculateTime(secs);
        progressBar.current.style.setProperty('--ms-seek-before-width', `${(progressBar.current.value / max) * 100 - 0.1}%`);
        setCurrentTime(currentDuration);
    };

    const calculateTime = (secs: any) => {
        if (secs === undefined) {
            return;
        }
        const minutes = Math.floor(secs / 60);
        const returnedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
        const seconds = Math.floor(secs % 60);
        const returnedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
        const totalDuration = `${returnedMinutes}:${returnedSeconds}`;

        return totalDuration;
    };

    const whilePlaying = () => {
        progressBar.current.value = audioPlayer.current.currentTime;
        const max = progressBar.current.max;
        progressBar.current.style.setProperty('--ms-seek-before-width', `${(progressBar.current.value / max) * 100 - 0.1}%`);
        setCurrentTime(calculateTime(progressBar.current.value));
        animationRef.current = requestAnimationFrame(whilePlaying);
    };

    const togglePlayPause = () => {
        setIsPlaying(!isPlaying);
        if (!isPlaying) {
            audioPlayer.current.play();
            animationRef.current = requestAnimationFrame(whilePlaying);
        } else {
            audioPlayer.current.pause();
            cancelAnimationFrame(animationRef.current);
        }
    };

    const moveBackward = () => {
        progressBar.current.value = Number(progressBar.current.value) - 10;
        getCurrentTime();
    };

    const moveForward = () => {
        progressBar.current.value = Number(progressBar.current.value) + 10;
        getCurrentTime();
    };

    const downloadAudio = async () => {
        try {
            saveAs(config.audioUrl as any, `${audioFileName}.mp3`);
        } catch (e) {
            console.log(e);
        }

        const element = document.createElement('a');
        let data: any = props.config.audioTranscript;
        const audioTextElement = document.createElement('p');
        audioTextElement.innerHTML = data;
        data = audioTextElement.textContent;
        const file = new Blob([data], { type: 'text/plain' });
        element.href = URL.createObjectURL(file);
        element.download = 'transcript.txt';
        document.body.appendChild(element); // Required for this to work in FireFox
        element.click();
        document.body.removeChild(element);
    };

    const toggleAudio = () => {
        audioPlayer.current.muted = !audioPlayer.current.muted;
        setIsMuted(!isMuted);
    };

    function handleParagraphChange(event: MsDyn365.ContentEditableEvent): void {
        props.config.audioTranscript = event.target.value;
    }

    function _getAudioDetailsParagraph(): React.ReactNode | undefined {
        const { audioTranscript } = props.config;

        return (
            transcript && (
                <MsDyn365.RichTextComponent
                    text={audioTranscript}
                    className='ms-video-player__details__text'
                    editProps={{ onEdit: handleParagraphChange, requestContext: props.context.request }}
                />
            )
        );
    }

    return (
        <Module {...AudioPlayerTag}>
            <Node {...AudioPlayerContainer}>
                <div tabIndex={0} className='ms-audio-player'>
                    <div className='ms-audio__top-container'>
                        <FiVolume2 className='ms-audio__volume-icon' />
                        <div className='ms-audio__top-download'>
                            <div onClick={downloadAudio} className='ms-audio__top-download-left'>
                                <a aria-label='Download Audio'>
                                    {toggleLang === MAORI_LANG ? resources.downloadTextInMaori : resources.downloadText} <FaDownload />
                                </a>
                            </div>
                        </div>
                    </div>
                    <div className='ms-audio__main-container'>
                        <h4>{audioTitle}</h4>
                        <p>{audioDesc}</p>
                    </div>
                    <div className='ms-audio__btm-container'>
                        <audio ref={audioPlayer} src={audioUrl} preload='metadata'></audio>
                        <div className='ms-audio__duration'>
                            <p>{currentTime}</p>
                            <p>{duration}</p>
                        </div>
                        <div className='ms-audio__rangeslider'>
                            <input
                                className='ms-audio__slider'
                                type='range'
                                defaultValue='0'
                                ref={progressBar}
                                onChange={getCurrentTime}
                                aria-label='Audio Range Slider'
                            />
                        </div>
                        <div className='ms-audio__controls'>
                            <div className='ms-audio__control-btn-left'>
                                <AiFillBackward onClick={moveBackward} className='ms-audio__backward-icon' />
                                {isPlaying ? (
                                    <FaPause className='ms-audio__pause-icon' onClick={togglePlayPause} />
                                ) : (
                                    <FaPlay className='ms-audio__play-icon' onClick={togglePlayPause} />
                                )}
                                <AiFillForward onClick={moveForward} className='ms-audio__forward-icon' />
                            </div>
                            <div className='ms-audio__control-btn-right'>
                                {isMuted ? (
                                    <FaVolumeMute onClick={toggleAudio} className='ms-audio__bottom-volume-icon' />
                                ) : (
                                    <IoMdVolumeHigh onClick={toggleAudio} className='ms-audio__bottom-volume-icon' />
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className='ms-audio-transcript'>
                    <div className='ms-audio-transcript__top-container'>
                        <div className='ms-audio-transcript__top-left'>
                            <MdOutlineSubject
                                style={{
                                    color: showTranscript ? '' : '#2b69b1',
                                    transition: 'all 0.3s ease-in-out'
                                }}
                                className='ms-audio-transcript__icon'
                            />
                            <span
                                style={{
                                    color: showTranscript ? '' : '#2b69b1',
                                    transition: 'all 0.3s ease-in-out'
                                }}
                            >
                                {toggleLang === MAORI_LANG ? resources.audioTranscriptTextInMaori : resources.audioTranscriptText}
                            </span>
                        </div>
                        <div className='ms-audio-transcript__top-right'>
                            <AiOutlinePlus
                                onClick={() => setShowTranscript(!showTranscript)}
                                className='ms-audio-transcript__plus-icon'
                                style={{
                                    transform: showTranscript ? 'rotate(45deg)' : 'rotate(0deg)',
                                    color: showTranscript ? '' : '#2b69b1',
                                    transition: 'all 0.3s ease-in-out',
                                    cursor: 'pointer',
                                    fontSize: '1rem'
                                }}
                            />
                        </div>
                    </div>
                    <div className='ms-audio-transcript__btm-container'>
                        <div tabIndex={0}>{showTranscript && <p>{_getAudioDetailsParagraph()}</p>}</div>
                    </div>
                </div>
            </Node>
        </Module>
    );
};
